import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, X } from 'lucide-react';

import { ApiError, api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ROLE_OPTIONS,
  type AdminScheduleSummary,
  type AdminUserDetail,
  type AdminUserSummary,
} from './types';
import type { Role } from '@/lib/auth-context';
import { RegionSelect } from './RegionSelect';

export function AdminUserNew() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const users = useQuery({
    queryKey: ['admin', 'users', { includeInactive: false }],
    queryFn: () => api<AdminUserSummary[]>('/admin/users'),
  });
  const schedules = useQuery({
    queryKey: ['admin', 'schedules'],
    queryFn: () => api<AdminScheduleSummary[]>('/admin/schedules'),
  });

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [role, setRole] = useState<Role>('EMPLOYEE');
  const [employeeCode, setEmployeeCode] = useState('');
  const [managerIds, setManagerIds] = useState<string[]>([]);
  const [scheduleId, setScheduleId] = useState<string>('');
  const [regionCode, setRegionCode] = useState<string>('ES-AN-SE');
  const [municipality, setMunicipality] = useState<string>('');

  const createMutation = useMutation({
    mutationFn: () =>
      api<AdminUserDetail>('/admin/users', {
        method: 'POST',
        body: JSON.stringify({
          email: email.trim(),
          fullName: fullName.trim(),
          role,
          employeeCode: employeeCode.trim() || undefined,
          managerIds: managerIds.length > 0 ? managerIds : undefined,
          scheduleId: scheduleId || undefined,
          regionCode,
          municipality,
        }),
      }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      navigate(`/admin/users/${created.id}`);
    },
  });

  const managerCandidates = (users.data ?? []).filter(
    (u) =>
      (u.role === 'MANAGER' || u.role === 'HR_ADMIN' || u.role === 'SUPER_ADMIN') && u.isActive,
  );
  const managerById = useMemo(
    () => new Map(managerCandidates.map((m) => [m.id, m])),
    [managerCandidates],
  );
  const availableManagers = useMemo(
    () => managerCandidates.filter((m) => !managerIds.includes(m.id)),
    [managerCandidates, managerIds],
  );

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate();
  }

  const error = createMutation.error
    ? createMutation.error instanceof ApiError
      ? createMutation.error.message
      : 'Error al crear el empleado'
    : null;

  const selectCls =
    'h-9 w-full rounded-sm border border-input bg-card px-3 text-base text-foreground focus-visible:border-icaria-600 focus-visible:outline-none transition-colors disabled:opacity-50';

  return (
    <div className="p-6 md:p-8 max-w-2xl mx-auto">
      <div className="mb-3">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-1.5 text-[12px] font-medium text-muted-foreground hover:text-icaria-600 transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Volver al listado
        </Link>
      </div>

      <div className="bg-card border border-border rounded-sm shadow-md overflow-hidden">
        <header className="px-7 py-5 border-b border-border">
          <h2 className="text-lg font-semibold tracking-tightish">Nuevo empleado</h2>
          <p className="text-[13px] text-muted-foreground mt-1">
            Se generará una contraseña temporal y se enviará por email. El usuario deberá cambiarla
            en el primer login.
          </p>
        </header>

        <form onSubmit={onSubmit} className="px-7 py-6 flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="new-email"
              type="email"
              required
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-name">
              Nombre completo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="new-name"
              type="text"
              required
              minLength={2}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-role">
              Rol <span className="text-destructive">*</span>
            </Label>
            <select
              id="new-role"
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              className={selectCls}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="new-code">Código de empleado (opcional)</Label>
            <Input
              id="new-code"
              type="text"
              value={employeeCode}
              onChange={(e) => setEmployeeCode(e.target.value)}
              placeholder="EMP-XXXX"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label>Managers (opcional)</Label>
            {managerIds.length === 0 ? (
              <p className="text-[12px] text-muted-foreground">— Sin managers asignados —</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {managerIds.map((id) => {
                  const m = managerById.get(id);
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-icaria-100 text-icaria-700 text-[12px] font-medium border border-icaria-200"
                    >
                      {m ? m.fullName : id}
                      <button
                        type="button"
                        onClick={() => setManagerIds((curr) => curr.filter((x) => x !== id))}
                        className="hover:text-icaria-600 transition-colors"
                        aria-label={`Quitar ${m?.fullName ?? id}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}

            {availableManagers.length > 0 ? (
              <select
                value=""
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setManagerIds((curr) => [...curr, v]);
                }}
                className={selectCls}
              >
                <option value="">— Añadir manager —</option>
                {availableManagers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName} ({m.email})
                  </option>
                ))}
              </select>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-schedule">Horario (opcional)</Label>
              <select
                id="new-schedule"
                value={scheduleId}
                onChange={(e) => setScheduleId(e.target.value)}
                className={selectCls}
              >
                <option value="">— Sin asignar —</option>
                {(schedules.data ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-region">Provincia (calendario)</Label>
              <RegionSelect id="new-region" value={regionCode} onChange={setRegionCode} />
              <span className="text-[11px] text-muted-foreground">
                Festivos que se excluyen en sus vacaciones.
              </span>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new-municipality">Municipio (opcional)</Label>
              <Input
                id="new-municipality"
                type="text"
                maxLength={120}
                value={municipality}
                onChange={(e) => setMunicipality(e.target.value)}
                placeholder="Ej. Lebrija"
              />
              <span className="text-[11px] text-muted-foreground">
                Si lo rellenas, también se le aplican los festivos locales de ese municipio.
              </span>
            </div>
          </div>

          {error ? (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          ) : null}

          <div className="flex items-center gap-2 pt-2">
            <Button type="submit" variant="primary" disabled={createMutation.isPending}>
              {createMutation.isPending ? 'Creando…' : 'Crear empleado'}
            </Button>
            <Button asChild variant="ghost">
              <Link to="/admin/users">Cancelar</Link>
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
